import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import Select from "react-select";
import { AsyncClientSelect, AsyncSourceSelect } from "components/Common/AsyncSearchSelects";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  Input,
  Row,
  Col,
  FormFeedback,
} from "reactstrap";
import * as Yup from "yup";
import { PatternFormat } from "react-number-format";
import { addCandidate, updateCandidate, getCandidateClientJobs } from "model/CandidateModel";
import {
  downloadFileFromUrl,
  toPhoneDigits,
  US_PHONE_INPUT_FORMAT,
} from "helpers/GeneralHelper";
import {
  applyAsyncFilterOptionToFormState,
  applyClientAsyncFilterOptionToFormState,
  buildAsyncFilterInitialOption,
  buildClientAsyncFilterInitialOption,
  getAsyncFilterLabelFieldKey,
  getClientAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

export const US_CITIZEN_SELECT_OPTIONS = [
  { value: -1, label: "Not specified" },
  { value: 1, label: "Yes" },
  { value: 0, label: "No" },
];

const selectZIndexStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};

/** e.g. user@gmail.com, user@ancile.services */
const CANDIDATE_EMAIL_REGEX = /^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/i;

const candidateFormSchema = Yup.object().shape({
  sourceId: Yup.string().trim().required("Source is required"),
  clientId: Yup.string().trim().required("Client is required"),
  jobId: Yup.string().trim().required("Position applied is required"),
  firstName: Yup.string().trim().required("First name is required"),
  lastName: Yup.string().trim().required("Last name is required"),
  phone: Yup.string()
    .trim()
    .required("Phone number is required")
    .matches(/^\d{10}$/, "Phone number must be 10 digits"),
  email: Yup.string()
    .trim()
    .test("email", "Wrong email", (value) => {
      const trimmed = String(value ?? "").trim();
      if (!trimmed) return true;
      return CANDIDATE_EMAIL_REGEX.test(trimmed);
    }),
});

const buildCandidateValidationValues = (form = {}) => ({
  sourceId: String(form?.sourceId ?? "").trim(),
  clientId: String(form?.clientId ?? "").trim(),
  jobId: String(form?.jobId ?? "").trim(),
  firstName: String(form?.firstName ?? "").trim(),
  lastName: String(form?.lastName ?? "").trim(),
  phone: toPhoneDigits(form?.phone).trim(),
  email: String(form?.email ?? "").trim(),
});

const SOURCE_ASYNC_FILTER_FIELD = "sourceId";

const getSourceAsyncFilterLabelFieldKey = () =>
  getAsyncFilterLabelFieldKey(SOURCE_ASYNC_FILTER_FIELD);

export const INITIAL_CANDIDATE_FORM = {
  id: "",
  sourceId: "",
  clientId: "",
  jobId: "",
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  city: "",
  state: "",
  specialNotes: "",
  status: "",
  orderStatus: "",
  profileStatus: "",
  recruiterName: "",
  drugStatus: "",
  mecStatus: "",
  funnlStatus: "",
  updateSend: false,
  fadvSend: false,
  isUsCitizen: -1,
  statusSwitch: true,
  markAsInEligible: false,
  resumeFile: null,
  resumeFileId: 0,
  confirm: false,
  [getSourceAsyncFilterLabelFieldKey()]: "",
  [getClientAsyncFilterLabelFieldKey()]: "",
};

/** Common list endpoints: unwrap records / data / array shape from API. */
const recordsFromCommonListResponse = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.records)) return payload.records;
  if (Array.isArray(payload.data)) return payload.data;
  const d = payload.data;
  if (d && Array.isArray(d.records)) return d.records;
  if (Array.isArray(d)) return d;
  return [];
};

const NEW_CANDIDATE_CREATE_DEFAULTS_KEY = "newCandidateCreateFormDefaults";

const readNewCandidateCreateDefaults = () => {
  try {
    const raw = localStorage.getItem(NEW_CANDIDATE_CREATE_DEFAULTS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      sourceId: String(parsed.sourceId ?? "").trim(),
      sourceIdLabel: String(parsed.sourceIdLabel ?? "").trim(),
      clientId: String(parsed.clientId ?? "").trim(),
      clientIdLabel: String(parsed.clientIdLabel ?? "").trim(),
      jobId: String(parsed.jobId ?? "").trim(),
      state: String(parsed.state ?? "").trim(),
    };
  } catch {
    return null;
  }
};

const saveNewCandidateCreateDefaults = ({
  sourceId = "",
  sourceIdLabel = "",
  clientId = "",
  clientIdLabel = "",
  jobId = "",
  state = "",
} = {}) => {
  try {
    const payload = {
      sourceId: String(sourceId ?? "").trim(),
      sourceIdLabel: String(sourceIdLabel ?? "").trim(),
      clientId: String(clientId ?? "").trim(),
      clientIdLabel: String(clientIdLabel ?? "").trim(),
      jobId: String(jobId ?? "").trim(),
      state: String(state ?? "").trim(),
    };
    if (!payload.sourceId && !payload.clientId && !payload.jobId) return;
    localStorage.setItem(NEW_CANDIDATE_CREATE_DEFAULTS_KEY, JSON.stringify(payload));
  } catch {
    // ignore storage errors
  }
};

const applyNewCandidateCreateDefaults = (form = {}, pageName = "") => {
  if (pageName !== "NewCandidate") return form;
  const saved = readNewCandidateCreateDefaults();
  if (!saved) return form;
  const clientLabelKey = getClientAsyncFilterLabelFieldKey();
  const sourceLabelKey = getSourceAsyncFilterLabelFieldKey();
  return {
    ...form,
    sourceId: saved.sourceId || form.sourceId,
    [sourceLabelKey]: saved.sourceIdLabel || form[sourceLabelKey],
    clientId: saved.clientId || form.clientId,
    [clientLabelKey]: saved.clientIdLabel || form[clientLabelKey],
    jobId: saved.jobId || form.jobId,
    state: saved.state || form.state,
  };
};

const buildFormFromCandidate = (candidate = {}, mode = "edit") => {
  const specialNotesValue = candidate?.specialNotes || "";
  const citizenRaw =
    candidate?.isUsCitizen !== undefined && candidate?.isUsCitizen !== null
      ? candidate?.isUsCitizen
      : candidate?.isUSCitizen !== undefined && candidate?.isUSCitizen !== null
        ? candidate?.isUSCitizen
        : -1;
  const statusRaw =
    candidate?.candidateStatus ?? candidate?.status ?? "";
  const statusSwitch =
    mode === "transfer"
      ? true
      : String(statusRaw || "active").toLowerCase() !== "deactive";

  return {
    ...INITIAL_CANDIDATE_FORM,
    id: candidate?.id || candidate?.candidateId || "",
    sourceId: candidate?.sourceId || "",
    [getSourceAsyncFilterLabelFieldKey()]:
      candidate?.sourceName || candidate?.source || "",
    clientId: mode === "transfer" ? "" : candidate?.clientId || "",
    [getClientAsyncFilterLabelFieldKey()]:
      mode === "transfer" ? "" : candidate?.clientName || candidate?.client || "",
    jobId: candidate?.jobId || "",
    firstName: candidate?.firstName || "",
    lastName: candidate?.lastName || "",
    phone: toPhoneDigits(candidate?.phone || ""),
    email: candidate?.email || "",
    city: candidate?.city || "",
    state: candidate?.state || "",
    specialNotes: specialNotesValue,
    status: mode === "transfer" ? "active" : statusRaw,
    orderStatus: candidate?.orderStatus || "",
    profileStatus: candidate?.profileStatus || "",
    recruiterName: candidate?.recruiterName || "",
    drugStatus: candidate?.drugStatus || "",
    mecStatus: candidate?.mecStatus || "",
    funnlStatus: candidate?.funnlStatus || "",
    updateSend: Boolean(candidate?.updateSend),
    fadvSend: Boolean(candidate?.fadvSend),
    isUsCitizen: citizenRaw === null || citizenRaw === undefined ? -1 : citizenRaw,
    statusSwitch,
    markAsInEligible:
      candidate?.inEligible === true ||
      candidate?.InEligible === true ||
      String(specialNotesValue).toLowerCase().includes("in-eligible"),
    resumeFile: null,
    resumeFileId: Number(candidate?.resumeFileId ?? candidate?.resumeUploadId) || 0,
    confirm: false,
  };
};

/**
 * Shared add / edit / transfer candidate modal (multipart updateCandidate / addCandidate API).
 */
const AddEditCandidateModal = ({
  isOpen,
  onClose,
  mode = "edit",
  candidate = {},
  onSuccess,
  /** When set (e.g. New Candidate search), phone is prefilled and not editable. */
  prefilledPhone = "",
  /** V2 POST `common/client` body `{ pageName, nameContains }` — e.g. `NewCandidate` maps to `CandidateFormRestrict`. */
  clientSelectPageName = "CandidateFormRestrict",
}) => {
  const lockedPhoneFromSearch = useMemo(
    () => toPhoneDigits(prefilledPhone).trim(),
    [prefilledPhone]
  );
  const isPhoneLocked = Boolean(lockedPhoneFromSearch);
  const [candidateForm, setCandidateForm] = useState(INITIAL_CANDIDATE_FORM);
  const [jobOptions, setJobOptions] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [errors, setErrors] = useState({});
  const candidateRef = useRef(candidate);
  candidateRef.current = candidate;
  const candidateId = candidate?.id ?? candidate?.candidateId ?? "";

  const resumeUploadLink = useMemo(
    () => String(candidate?.resumeUploadLink ?? "").trim(),
    [candidate?.resumeUploadLink]
  );

  /** Load jobs for the selected client via GET `Candidate/clients/{clientId}/jobs`. */
  const fetchJobsByClientId = useCallback(async (clientId) => {
    const id = String(clientId || "").trim();
    if (!id) {
      setJobOptions([]);
      return;
    }
    setJobsLoading(true);
    try {
      const response = await getCandidateClientJobs(id);
      const jobRecords = recordsFromCommonListResponse(response?.data ?? response);
      const options = (Array.isArray(jobRecords) ? jobRecords : [])
        .map((item) => {
          const value = String(item?.jobId ?? item?.id ?? item?._id ?? "").trim();
          if (!value) return null;
          const label =
            String(
              item?.job ?? item?.jobName ?? item?.name ?? item?.title ?? item?.display ?? ""
            ).trim() || value;
          return { value, label };
        })
        .filter(Boolean);
      setJobOptions(options);
    } catch (err) {
      setJobOptions([]);
      toast.error(err?.message || "Unable to load jobs for client");
    } finally {
      setJobsLoading(false);
    }
  }, []);

  const clearFieldError = (fieldName) => {
    setErrors((prev) => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const validateEmailField = (rawEmail) => {
    const trimmed = String(rawEmail ?? "").trim();
    if (!trimmed) {
      clearFieldError("email");
      return;
    }
    try {
      candidateFormSchema.validateSyncAt("email", { email: trimmed });
      clearFieldError("email");
    } catch (validationError) {
      setErrors((prev) => ({
        ...prev,
        email: validationError?.message || "Wrong email",
      }));
    }
  };

  const handleSourceChange = (selectedOption) => {
    setCandidateForm((prev) =>
      applyAsyncFilterOptionToFormState(prev, SOURCE_ASYNC_FILTER_FIELD, selectedOption)
    );
    clearFieldError("sourceId");
  };

  const handleClientChange = (selectedOption) => {
    const nextClientId = selectedOption?.value ?? "";
    setCandidateForm((prev) => ({
      ...applyClientAsyncFilterOptionToFormState(prev, selectedOption),
      jobId: "",
    }));
    clearFieldError("clientId");
    clearFieldError("jobId");
    fetchJobsByClientId(nextClientId);
  };

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      let nextForm = buildFormFromCandidate(candidateRef.current, mode);
      if (lockedPhoneFromSearch) {
        nextForm.phone = lockedPhoneFromSearch;
      }
      if (mode === "create") {
        nextForm = applyNewCandidateCreateDefaults(nextForm, clientSelectPageName);
      }
      setCandidateForm(nextForm);
      setErrors({});
      setJobOptions([]);
      if (nextForm?.clientId) {
        await fetchJobsByClientId(nextForm.clientId);
      }
      if (cancelled) return;
      setFormKey((prev) => prev + 1);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    mode,
    candidateId,
    fetchJobsByClientId,
    lockedPhoneFromSearch,
    clientSelectPageName,
  ]);

  const handleCandidateFormChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    const nextValue = type === "checkbox" ? checked : files ? files[0] : value;
    setCandidateForm((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
    if (name === "email") {
      validateEmailField(nextValue);
      return;
    }
    if (candidateFormSchema.fields[name]) {
      clearFieldError(name);
    }
  };

  const handlePhoneValueChange = (values) => {
    const nextValue = toPhoneDigits(values?.value || "");
    setCandidateForm((prev) => ({
      ...prev,
      phone: nextValue,
    }));
    if (candidateFormSchema.fields.phone) {
      clearFieldError("phone");
    }
  };

  const handlePhonePaste = (e) => {
    // PatternFormat truncates to 10 digits before onValueChange;
    // intercept paste so +1 country code is stripped first.
    if (isPhoneLocked) return;
    const pasted = e.clipboardData?.getData("text") || "";
    const digits = String(pasted).replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) {
      e.preventDefault();
      const nextValue = toPhoneDigits(pasted);
      setCandidateForm((prev) => ({
        ...prev,
        phone: nextValue,
      }));
      if (candidateFormSchema.fields.phone) {
        clearFieldError("phone");
      }
    }
  };

  const updateCandidateSelect = (fieldName, selectedOption) => {
    if (fieldName === "isUsCitizen") {
      const item = selectedOption?.value;
      const next = item === "" || item === undefined || item === null ? -1 : Number(item);
      setCandidateForm((prev) => ({ ...prev, isUsCitizen: next }));
      return;
    }
    setCandidateForm((prev) => ({
      ...prev,
      [fieldName]: selectedOption?.value ?? "",
    }));
    if (candidateFormSchema.fields[fieldName]) {
      clearFieldError(fieldName);
    }
  };

  const closeModal = () => {
    setCandidateForm(INITIAL_CANDIDATE_FORM);
    setErrors({});
    setJobOptions([]);
    setFormKey((prev) => prev + 1);
    onClose?.();
  };

  const handleCandidateSave = async (e) => {
    e.preventDefault();
    const candidateResourceId = candidateForm?.id || candidateForm?.candidateId || "";
    if (mode !== "create" && !candidateResourceId) {
      toast.error("Candidate ID not found");
      return;
    }

    try {
      await candidateFormSchema.validate(buildCandidateValidationValues(candidateForm), {
        abortEarly: false,
      });
      setErrors({});
    } catch (validationError) {
      const validationErrors = {};
      validationError?.inner?.forEach((errorItem) => {
        if (errorItem?.path && !validationErrors[errorItem.path]) {
          validationErrors[errorItem.path] = errorItem.message;
        }
      });
      setErrors(validationErrors);
      return;
    }

    const notesTrimmed = String(candidateForm?.specialNotes || "").trim();
    const notesValue = notesTrimmed || null;
    const candidateStatus = candidateForm?.statusSwitch ? "active" : "deactive";

    const appendMultipartScalar = (formData, key, value) => {
      const isEmpty =
        value === undefined ||
        value === null ||
        (typeof value === "string" && value.trim() === "");
      if (isEmpty) {
        formData.append(key, "");
        return;
      }
      formData.append(key, value);
    };

    const buildCandidateMultipartFormData = (confirmOverride) => {
      const formData = new FormData();
      appendMultipartScalar(formData, "SourceId", candidateForm?.sourceId || null);
      appendMultipartScalar(formData, "FirstName", candidateForm?.firstName || null);
      appendMultipartScalar(formData, "LastName", candidateForm?.lastName || null);
      appendMultipartScalar(formData, "Phone", toPhoneDigits(candidateForm?.phone) || null);
      appendMultipartScalar(formData, "Email", candidateForm?.email || null);
      appendMultipartScalar(formData, "ClientId", candidateForm?.clientId || null);
      appendMultipartScalar(formData, "JobId", candidateForm?.jobId || null);
      appendMultipartScalar(formData, "CandidateStatus", candidateStatus);
      appendMultipartScalar(formData, "City", candidateForm?.city || "");
      appendMultipartScalar(formData, "State", candidateForm?.state || "");

      const rawCitizen = candidateForm?.isUsCitizen;
      const citizenNum =
        rawCitizen === undefined || rawCitizen === null || rawCitizen === ""
          ? NaN
          : Number(rawCitizen);
      if (!Number.isFinite(citizenNum) || citizenNum === -1) {
        appendMultipartScalar(formData, "IsUSCitizen", -1);
      } else {
        appendMultipartScalar(formData, "IsUSCitizen", String(citizenNum));
      }

      appendMultipartScalar(formData, "Notes", notesValue);
      formData.append("InEligible", Boolean(candidateForm?.markAsInEligible));

      const resume = candidateForm?.resumeFile;
      if (resume instanceof File) {
        formData.append("ResumeFile", resume);
      } 
      // else {
      //   formData.append("ResumeFile", new Blob([]));
      // }

      const resumeId = Number(candidateForm?.resumeFileId ?? 0);
      formData.append("ResumeFileId", Number.isFinite(resumeId) ? resumeId : 0);
      formData.append("ActionType", mode === "transfer" ? "Transfer" : "Edit");
      const confirmVal =
        confirmOverride !== undefined
          ? Boolean(confirmOverride)
          : Boolean(candidateForm?.confirm);
      formData.append("Confirm", confirmVal);

      return formData;
    };

    const executeCandidateSave = async (confirmFlag) => {
      const fd = buildCandidateMultipartFormData(confirmFlag);
      if (mode === "transfer" || mode === "edit") {
        return updateCandidate(candidateResourceId, fd);
      }
      return addCandidate(fd);
    };

    const isConfirmRequiredResponse = (res) => {
      if (!res || res?.success === true) return false;
      const code = String(res?.error?.code ?? "").toUpperCase();
      return code === "CONFIRM_REQUIRED";
    };

    setSaving(true);
    try {
      let response = await executeCandidateSave();

      if (isConfirmRequiredResponse(response)) {
        const confirmMessage =
          response?.error?.message ||
          response?.message ||
          "Please confirm to continue.";
        const swalResult = await Swal.fire({
          title: "Confirm",
          text: confirmMessage,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "OK",
          cancelButtonText: "Cancel",
          target: document.body,
          allowOutsideClick: false,
          didOpen: () => {
            const el = document.querySelector(".swal2-container");
            if (el) el.style.zIndex = 20000;
          },
        });
        if (!swalResult.isConfirmed) {
          return;
        }
        response = await executeCandidateSave(true);
      }

      if (response?.success || response?.isSuccess || response?.status) {
        toast.success(
          response?.message ||
            (mode === "transfer"
              ? "Candidate transferred successfully"
              : mode === "create"
                ? "Candidate created successfully"
                : "Candidate updated successfully")
        );
        if (mode === "create") {
          if (clientSelectPageName === "NewCandidate") {
            saveNewCandidateCreateDefaults({
              sourceId: candidateForm?.sourceId,
              sourceIdLabel: candidateForm?.[getSourceAsyncFilterLabelFieldKey()],
              clientId: candidateForm?.clientId,
              clientIdLabel: candidateForm?.[getClientAsyncFilterLabelFieldKey()],
              jobId: candidateForm?.jobId,
              state: candidateForm?.state,
            });
          }
          onSuccess?.();
        } else {
          const raw = candidateRef.current || {};
          const citizenRaw = candidateForm?.isUsCitizen;
          const citizenNum =
            citizenRaw === undefined || citizenRaw === null || citizenRaw === ""
              ? NaN
              : Number(citizenRaw);
          const citizenResolved = Number.isFinite(citizenNum) ? citizenNum : (raw?.isUsCitizen ?? raw?.isUSCitizen);
          const sourceLabel = candidateForm?.[getSourceAsyncFilterLabelFieldKey()];
          const jobOpt = jobOptions.find(
            (o) => String(o.value) === String(candidateForm?.jobId || "")
          );
          const patchForList = {
            ...raw,
            id: candidateForm?.id || raw.id,
            candidateId: candidateForm?.id || raw.candidateId || raw.id,
            sourceId: candidateForm?.sourceId ?? raw.sourceId,
            clientId: candidateForm?.clientId ?? raw.clientId,
            jobId: candidateForm?.jobId ?? raw.jobId,
            firstName: candidateForm?.firstName,
            lastName: candidateForm?.lastName,
            phone: toPhoneDigits(candidateForm?.phone),
            email: candidateForm?.email,
            city: candidateForm?.city,
            state: candidateForm?.state,
            specialNotes: notesValue,
            candidateStatus: candidateStatus,
            status: candidateStatus,
            isUsCitizen: citizenResolved,
            isUSCitizen: citizenResolved,
            resumeFileId: Number(candidateForm?.resumeFileId ?? 0) || raw.resumeFileId,
            sourceName: sourceLabel || raw.sourceName,
            source: sourceLabel || raw.source,
            jobName: jobOpt?.label ?? raw.jobName,
            job: jobOpt?.label ?? raw.job,
            clientName:
              candidateForm?.[getClientAsyncFilterLabelFieldKey()] || raw.clientName,
            client: candidateForm?.[getClientAsyncFilterLabelFieldKey()] || raw.client,
          };
          onSuccess?.(patchForList);
        }
        closeModal();
      } 
      // else {
      //   toast.error(
      //     response?.message ||
      //       response?.error?.message ||
      //       "Unable to save candidate"
      //   );
      // }
    } catch (err) {
      toast.error(
        err?.response?.data?.error?.message ||
          err?.message ||
          "Unable to save candidate"
      );
    } finally {
      setSaving(false);
    }
  };

  const modalTitle =
    mode === "transfer"
      ? "Transfer Candidate"
      : mode === "edit"
        ? "Edit Candidate"
        : "Candidate Form";

  return (
    <Modal
      isOpen={isOpen}
      toggle={closeModal}
      size="lg"
      centered
      backdrop="static"
      className="modal-full-height"
    >
      <ModalBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0">{modalTitle}</h5>
          <Button close onClick={closeModal} />
        </div>
        <hr />

        <Form key={formKey} onSubmit={handleCandidateSave}>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Source <span className="text-danger">*</span></Label>
                  <div className={errors.sourceId ? "is-invalid" : ""}>
                    <AsyncSourceSelect
                      key={`add-edit-candidate-source-${candidateForm.sourceId}-${formKey}`}
                      value={String(candidateForm?.sourceId || "").trim()}
                      initialOption={buildAsyncFilterInitialOption(
                        SOURCE_ASYNC_FILTER_FIELD,
                        candidateForm.sourceId,
                        {
                          [SOURCE_ASYNC_FILTER_FIELD]: candidateForm.sourceId,
                          [getSourceAsyncFilterLabelFieldKey()]:
                            candidateForm?.[getSourceAsyncFilterLabelFieldKey()],
                        }
                      )}
                      onChange={handleSourceChange}
                      placeholder="Select Source"
                    />
                  </div>
                  {errors.sourceId ? (
                    <div className="invalid-feedback d-block">{errors.sourceId}</div>
                  ) : null}
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label>Client <span className="text-danger">*</span></Label>
                  <div className={errors.clientId ? "is-invalid" : ""}>
                    <AsyncClientSelect
                      key={`add-edit-candidate-client-${candidateForm.clientId}-${formKey}`}
                      value={String(candidateForm?.clientId || "").trim()}
                      pageName={clientSelectPageName}
                      initialOption={buildClientAsyncFilterInitialOption(
                        candidateForm.clientId,
                        candidateForm[getClientAsyncFilterLabelFieldKey()]
                      )}
                      onChange={handleClientChange}
                      placeholder="Select Client"
                      isClearable
                      isDisabled={Boolean(candidateId) && mode !== "transfer"}
                    />
                  </div>
                  {errors.clientId ? (
                    <div className="invalid-feedback d-block">{errors.clientId}</div>
                  ) : null}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Position Applied <span className="text-danger">*</span></Label>
                  <Select
                    options={jobOptions}
                    className={`select2-selection${errors.jobId ? " is-invalid" : ""}`}
                    placeholder={
                      candidateForm?.clientId
                        ? "Select Position"
                        : "Select client first"
                    }
                    isLoading={jobsLoading}
                    isDisabled={!String(candidateForm?.clientId || "").trim()}
                    value={
                      jobOptions.find(
                        (opt) =>
                          String(opt.value) === String(candidateForm?.jobId || "")
                      ) || null
                    }
                    onChange={(selectedOption) =>
                      updateCandidateSelect("jobId", selectedOption)
                    }
                    menuPortalTarget={document.body}
                    styles={selectZIndexStyles}
                  />
                  {errors.jobId ? (
                    <div className="invalid-feedback d-block">{errors.jobId}</div>
                  ) : null}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>First Name <span className="text-danger">*</span></Label>
                  <Input
                    name="firstName"
                    value={candidateForm?.firstName || ""}
                    onChange={handleCandidateFormChange}
                    invalid={!!errors.firstName}
                  />
                  <FormFeedback>{errors.firstName}</FormFeedback>
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label>Last Name <span className="text-danger">*</span></Label>
                  <Input
                    name="lastName"
                    value={candidateForm?.lastName || ""}
                    onChange={handleCandidateFormChange}
                    invalid={!!errors.lastName}
                  />
                  <FormFeedback>{errors.lastName}</FormFeedback>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Phone <span className="text-danger">*</span></Label>
                  <PatternFormat
                    customInput={Input}
                    format={US_PHONE_INPUT_FORMAT}
                    allowEmptyFormatting
                    name="phone"
                    type="text"
                    inputMode="numeric"
                    value={candidateForm?.phone || ""}
                    onValueChange={handlePhoneValueChange}
                    onPaste={handlePhonePaste}
                    // readOnly={isPhoneLocked}
                    // disabled={isPhoneLocked}
                    invalid={!!errors.phone}
                  />
                  <FormFeedback>{errors.phone}</FormFeedback>
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label>Email</Label>
                  <Input
                    name="email"
                    type="text"
                    autoComplete="email"
                    value={candidateForm?.email || ""}
                    onChange={handleCandidateFormChange}
                    invalid={!!errors.email}
                  />
                  <FormFeedback>{errors.email}</FormFeedback>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Is US Citizens</Label>
                  <Select
                    options={US_CITIZEN_SELECT_OPTIONS}
                    className="select2-selection"
                    placeholder="Select"
                    value={
                      US_CITIZEN_SELECT_OPTIONS.find(
                        (opt) =>
                          String(opt.value) === String(candidateForm?.isUsCitizen ?? -1)
                      ) || null
                    }
                    onChange={(selectedOption) =>
                      updateCandidateSelect("isUsCitizen", selectedOption)
                    }
                    menuPortalTarget={document.body}
                    styles={selectZIndexStyles}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>City</Label>
                  <Input
                    name="city"
                    value={candidateForm?.city || ""}
                    onChange={handleCandidateFormChange}
                  />
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label>State</Label>
                  <Input
                    name="state"
                    value={candidateForm?.state || ""}
                    onChange={handleCandidateFormChange}
                  />
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label>Resume upload (doc, pdf, docx, png, jpeg, jpg)</Label>
              <Input
                type="file"
                name="resumeFile"
                accept=".doc,.docx,.pdf,image/png,image/gif,image/jpeg,image/jpg"
                onChange={handleCandidateFormChange}
              />
              {resumeUploadLink ? (
                <div className="mt-2">
                  <button
                    type="button"
                    className="cd-profile-image-view-link cd-profile-image-view-link--no-offset"
                    title="Download candidate resume"
                    onClick={() => downloadFileFromUrl(resumeUploadLink)}
                  >
                    <span
                      className="cd-profile-image-view-link__icon"
                      aria-hidden="true"
                    >
                      <i className="fa fa-file" />
                    </span>
                    <span className="cd-profile-image-view-link__text">
                      Download CV
                    </span>
                    <i
                      className="fa fa-external-link-alt cd-profile-image-view-link__external"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              ) : null}
            </FormGroup>

            <FormGroup>
              <Label>Special Notes</Label>
              <Input
                type="textarea"
                rows="3"
                name="specialNotes"
                value={candidateForm?.specialNotes || ""}
                onChange={handleCandidateFormChange}
              />
            </FormGroup>

            <FormGroup check className="mt-2 mb-3">
              <Input
                type="checkbox"
                name="markAsInEligible"
                defaultChecked={candidateForm?.markAsInEligible}
                onChange={handleCandidateFormChange}
              />
              <Label check>Mark as In-Eligible</Label>
            </FormGroup>

            <FormGroup>
              <Label>
                Status <span className="text-danger">*</span>
              </Label>
              <div className="input-box d-flex align-items-center justify-content-between">
                <span className="text-muted">Type</span>
                <div className="d-flex align-items-center">
                  <strong className="me-2">
                    {candidateForm?.statusSwitch ? "Active" : "Deactive"}
                  </strong>
                  <div className="form-switch">
                    <Input
                      type="switch"
                      name="statusSwitch"
                      defaultChecked={candidateForm?.statusSwitch}
                      onChange={handleCandidateFormChange}
                      className="form-check-input-padding"
                    />
                  </div>
                </div>
              </div>
            </FormGroup>

            <div className="text-end">
              {mode === "create" ? (
                <Button color="primary" type="submit" disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </Button>
              ) : (
                <Button
                  color={mode === "transfer" ? "warning" : "primary"}
                  type="submit"
                  disabled={saving}
                >
                  {saving
                    ? "Saving..."
                    : mode === "transfer"
                      ? "Transfer"
                      : "Update"}
                </Button>
              )}
            </div>
          </Form>
      </ModalBody>
    </Modal>
  );
};

export default AddEditCandidateModal;
